import { api } from '@/lib/axios'
import type { ExportRequest } from '@/lib/export-request'
import type { BillingWorkplaceTab, RegieProposal, SalesLineState, SalesLineType, SalesLineView } from '@symphenia/shared-types'

/** AP-35 Phase H — Verkaufszeilen, Regievorschlag, Abrechnung (Detailblatt 7). */

export type SalesLine = SalesLineView

export interface SalesTotals {
  lines: number
  open: number
  delivered: number
  invoiced: number
  value: number | null
  deliveredValue: number | null
  invoicedValue: number | null
  openValue: number | null
}

export interface SalesLineInput {
  lineType?: SalesLineType
  subMode?: 'SUM_OF_CHILDREN' | 'OWN_PRICE' | null
  quantity?: number
  unitId?: string | null
  altQuantity?: number | null
  altUnitCode?: string | null
  name?: string
  longText?: string | null
  introText?: string | null
  customerReference?: string | null
  articleId?: string | null
  unitPrice?: number | null
  priceUnit?: number
  discountPercent?: number | null
  taxRate?: number | null
  isBillable?: boolean
  billableFrom?: string | null
  internalNote?: string | null
  deliveredAt?: string | null
  parentId?: string | null
}

/** Nachtrag 12: Reihenfolge **und** Ebene — Ziehen und Einrücken sind dieselbe Bewegung. */
export interface SalesLineOrder {
  id: string
  parentId: string | null
}

const pos = (projectId: string, positionId: string) => `/projects/${projectId}/positions/${positionId}/sales-lines`

export async function fetchSalesLines(projectId: string, positionId: string): Promise<{ lines: SalesLine[]; totals: SalesTotals }> {
  return (await api.get(pos(projectId, positionId))).data.data
}

export async function createSalesLine(projectId: string, positionId: string, dto: SalesLineInput & { name: string }): Promise<SalesLine> {
  return (await api.post(pos(projectId, positionId), dto)).data.data
}

export async function deleteSalesLine(id: string): Promise<void> {
  await api.delete(`/projects/sales-lines/${id}`)
}

export async function splitSalesLine(id: string, quantity: number) {
  return (await api.post(`/projects/sales-lines/${id}/split`, { quantity })).data.data as { lines: SalesLine[]; totals: SalesTotals }
}

export async function reorderSalesLines(projectId: string, positionId: string, lines: SalesLineOrder[]) {
  return (await api.post(`${pos(projectId, positionId)}/reorder`, { lines })).data.data as { lines: SalesLine[]; totals: SalesTotals }
}

export async function copySalesLines(lineIds: string[]): Promise<number> {
  return (await api.post('/projects/sales-lines/copy', { lineIds })).data.data.copied
}

export async function pasteSalesLines(projectId: string, positionId: string, itemIds: string[]) {
  return (await api.post(`${pos(projectId, positionId)}/paste`, { itemIds })).data.data as { lines: SalesLine[]; totals: SalesTotals }
}

// ── Regievorschlag ──────────────────────────────────────────────────────────

export async function fetchRegieProposal(positionId: string, until?: string): Promise<RegieProposal> {
  return (await api.get('/project-billing/regie-proposal', { params: { positionId, until } })).data.data
}

export async function acceptRegieProposal(dto: { positionId: string; until?: string; timeEntryIds: string[]; consumableIds?: string[]; confirmNotTimeAndMaterial?: boolean }) {
  return (await api.post('/project-billing/regie-proposal/accept', dto)).data.data
}

// ── Abrechnungsliste und Arbeitsplatz ───────────────────────────────────────

export interface BillingDocument {
  id: string
  kind: string
  number: string
  status: string
  paymentStatus: string
  date: string | null
  netTotal?: number
  grossTotal?: number
}

export interface ProjectBilling {
  project: { id: string; projectNumber: string; name: string; type: string; status: string; client: { id: string; name: string } | null }
  scheme: string
  interval: string
  totals: SalesTotals & { supplementValue: number | null }
  progressPercent: number
  billedPercent: number
  orderConfirmation: { id: string; number: string | null; netTotal?: number } | null
  billingDone: { at: string; userId: string | null; note: string | null } | null
  groups: {
    position: { id: string; number: number; name: string; status: string; type: string; scheme: string }
    lines: SalesLine[]
    totals: SalesTotals
  }[]
  direct: {
    id: string
    name: string
    quantity: number
    unitCode: string | null
    lineNet?: number
    positionId: string | null
    positionNumber: string | null
    document: { id: string; kind: string; number: string | null; date: string | null }
  }[]
  documents: BillingDocument[]
  canCreateDocuments: boolean
}

export async function fetchProjectBilling(projectId: string): Promise<ProjectBilling> {
  return (await api.get(`/projects/${projectId}/billing`)).data.data
}

export interface WorkplaceFilter {
  clientId?: string
  projectId?: string
  projectLeadUserId?: string
  q?: string
  scheme?: string
  /** Filterleiste: Projektnummer oder -bezeichnung enthält. */
  projectSearch?: string
  /** Filterleiste: Positionsbezeichnung enthält; eine Zahl trifft auch die Nummer. */
  positionSearch?: string
  /** Filterleiste: Name des Kunden enthält. */
  clientName?: string
}

export type WorkplaceRow = {
  kind: 'LINE' | 'REGIE' | 'PARTIAL' | 'RATE' | 'PROJECT'
  id: string
  project: { id: string; projectNumber: string; name: string }
  client: { id: string; name: string } | null
  position?: { id: string; number: number; name: string; status: string }
  // LINE
  name?: string
  quantity?: number
  unitCode?: string | null
  lineTotal?: number | null
  state?: SalesLineState
  deliveredAt?: string | null
  deliveryNote?: { id: string; number: string | null } | null
  inDrafts?: string[]
  // REGIE
  hours?: number
  days?: number
  since?: string | null
  // PARTIAL
  progressPercent?: number
  billedPercent?: number
  orderConfirmation?: string | null
  // RATE
  label?: string
  percent?: number | null
  amount?: number | null
  due?: boolean
  // PROJECT
  scheme?: string
  interval?: string
  dueAt?: string | null
  lastInvoiceAt?: string | null
  openLines?: number
  regieHours?: number
  status?: string
  billingDoneAt?: string | null
}

/** Phase K (H-19) — Export eines Reiters des Arbeitsplatzes (Mailweg `EXPORT_PROJECT_BILLING`). */
export function workplaceExportRequest(
  tab: BillingWorkplaceTab,
  params: WorkplaceFilter & { format: string; orientation?: string },
  columns: { key: string; label: string }[],
): ExportRequest {
  return { url: `/project-billing/${tab}/export`, params: { ...params, columns: JSON.stringify(columns) } }
}

export async function fetchWorkplace(tab: BillingWorkplaceTab, filter: WorkplaceFilter): Promise<WorkplaceRow[]> {
  return (await api.get(`/project-billing/${tab}`, { params: filter })).data.data
}

export interface CreateDocumentsResult {
  dryRun: boolean
  groups: { clientId: string; clientName: string; projectId: string | null; projects: string[]; lines: { lineId: string; name: string; project: string; position: number; quantity: number; unitCode: string | null }[]; total?: number }[]
  skipped: { lineId: string; name: string; reason: string }[]
  created: { documentId: string; clientName: string; lines: number }[]
  warning: string | null
}

export async function createBillingDocuments(dto: {
  lineIds: string[]
  kind: 'DELIVERY_NOTE' | 'INVOICE'
  split?: 'PER_CUSTOMER' | 'PER_PROJECT'
  deliveryAddress?: 'NONE' | 'PROJECT_SITE'
  dryRun?: boolean
}): Promise<CreateDocumentsResult> {
  return (await api.post('/project-billing/create-documents', dto)).data.data
}

export async function createPartialInvoice(dto: { projectId: string; percent: number; kind?: 'PARTIAL_INVOICE' | 'DOWN_PAYMENT_INVOICE'; dryRun?: boolean }) {
  return (await api.post('/project-billing/partial-invoice', dto)).data.data as {
    dryRun: boolean
    orderConfirmation: { id: string; number: string | null }
    billedPercent: number
    targetPercent: number
    invoicePercent: number
    documentId?: string
  }
}

export async function invoicePaymentPlanItem(itemId: string) {
  return (await api.post(`/project-billing/payment-plan/${itemId}/invoice`)).data.data as { documentId: string; percent: number; kind: string }
}

export async function setBillingDone(projectId: string, note?: string) {
  return (await api.post(`/projects/${projectId}/billing-done`, { note })).data
}

export async function revokeBillingDone(projectId: string, reason: string) {
  return (await api.post(`/projects/${projectId}/billing-done/revoke`, { reason })).data
}

export async function setPositionBillingDone(projectId: string, positionId: string, note?: string) {
  return (await api.post(`/projects/${projectId}/positions/${positionId}/billing-done`, { note })).data as {
    data: unknown
    hint?: { code: string; openLines: number }
  }
}

export async function projectFromDocument(documentId: string) {
  return (await api.post('/projects/from-document', { documentId })).data.data as { projectId: string; positionIds: string[] }
}

export async function searchArticles(q: string): Promise<{ id: string; name: string; articleNumber: string; unit: { id: string; code: string } | null }[]> {
  return (await api.get('/projects/lookups/articles', { params: { q } })).data.data
}
